import React, { useCallback, useEffect, useState } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { FormattedMessage, useIntl } from 'react-intl';
import { List as ImmutableList } from 'immutable';
import { Box, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { CheckInBlock } from 'common/components/PersonProfile/components';
import { LoadingBackdrop } from 'common/components';
import ProductItem from './ProductItem';
import {
  CartUnitTypes,
  IInvoiceDetailsDto,
  IInvoiceProductItem,
  InvoiceStatus,
  MenuItemTypes,
} from 'common/interfaces/invoices';
import {
  ISimpleClubInventoryItem,
  ISimpleClubInventoryItemImt,
} from 'modules/pos-settings/interfaces/inventoryItems';
import {
  selectPersonSuggestedProducts,
  selectPersonSuggestedProductsLoading,
} from 'common/components/PersonProfile/state/selectors';
import { selectSelectedRegisterId } from 'modules/pos-kiosk/state/register/selectors';
import * as selectors from 'modules/authentication/state/selectors';
import {
  selectAddInvoiceUnitActionLoading,
  selectIsUpdateInvoiceUnitLoading,
} from 'common/state/invoice/selectors';
import * as actions from 'common/components/PersonProfile/state/actions';
import * as actionsInvoice from 'common/state/invoice/actions';
import { PeakModules } from 'common/constants/peakModules';
import { usePersonSelectorTemplate } from 'common/components/PersonProfile/hooks/usePersonSelector';
import { useAppDispatch } from 'store/hooks';
import messages from 'common/components/PersonProfile/messages';
import StorageServices from 'services/storage';
import { ADD_INVOICE_UNIT, CREATE_INVOICE } from 'common/constants/delayedActionsKeys';
import { IUserOrganizationImt } from 'common/interfaces/clients';
import { PermissionLevels } from 'common/constants/permissions';
import useFetchInvoicesList from 'common/httpHooks/useFetchInvoicesList';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    height: '100%',
    minHeight: '150px',
  },
  padding: {
    padding: theme.spacing(2, 0, 0),
  },
}));

interface IProps {
  personId: number;
  openPOSPanel?: () => void;
  isOpenPOSPanel?: boolean;
}

const { addDelayedAction } = StorageServices.delayedActions;

const SuggestedProducts = ({ personId, openPOSPanel, isOpenPOSPanel }: IProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const [isInvoicesListLoading, setIsInvoicesListLoading] = useState(false);

  const usePersonSelector = usePersonSelectorTemplate(personId);

  const products: ImmutableList<ISimpleClubInventoryItemImt> = usePersonSelector(
    selectPersonSuggestedProducts,
  );
  const isProductsLoading: boolean = usePersonSelector(selectPersonSuggestedProductsLoading);
  const selectedRegisterId: string = useRootSelector(selectSelectedRegisterId);
  const selectedOrg: IUserOrganizationImt = useRootSelector(
    selectors.selectUserSelectedOrganization,
  );
  const isAddInvoiceUnitActionLoading: boolean = useRootSelector(
    selectAddInvoiceUnitActionLoading(),
  );
  const isUpdateInvoiceUnitLoading = useRootSelector(selectIsUpdateInvoiceUnitLoading);

  const classes = useStyles();

  const fetchInvoicesList = useFetchInvoicesList({
    module: PeakModules.FrontDesk,
    registerId: selectedRegisterId,
    isMembershipStep: false,
  });

  const fetchPaymentsHistory = useCallback(() => {
    dispatch(actions.fetchRecentPersonPayments(personId, PeakModules.FrontDesk));
  }, [dispatch, personId]);

  useEffect(() => {
    if (personId && selectedOrg.get('permissionsLevel') !== PermissionLevels.CORPORATE) {
      dispatch(actions.fetchPersonSuggestedProducts(personId, selectedOrg.get('id')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, personId]);

  const handleCreateOrUpdateInvoice = useCallback(
    (product: ISimpleClubInventoryItem, invoicesList: IInvoiceDetailsDto[]) => {
      const newProduct: IInvoiceProductItem = {
        inventoryId: product.id,
        inventoryClubId: product.id,
        title: product.title,
        imageUrl: product.imageUrl,
        taxAmount: product.taxAmount,
        onHandAmount: product.onHandAmount,
        order: 0,
        number: 1,
        type: MenuItemTypes.Item,
        price: product.retailPrice,
      };

      const openedInvoiceForSelectedCustomer: IInvoiceDetailsDto | void = invoicesList?.find(
        (invoiceItem: IInvoiceDetailsDto) => {
          return invoiceItem.status === InvoiceStatus.OPEN && invoiceItem.customer?.id === personId;
        },
      );

      // TODO need to increment open invoices count in register selector DTO(websocket)
      const doAction = (registerId: string) => {
        dispatch(
          actionsInvoice.addInvoiceUnit(
            null,
            {
              createNewInvoice: true,
              inventoryClubs: [newProduct],
              memberId: personId,
              registerId,
            },
            CartUnitTypes.INVENTORY,
            PeakModules.FrontDesk,
            true,
            true,
            fetchPaymentsHistory,
          ),
        );
      };

      if (openedInvoiceForSelectedCustomer) {
        const existedInvoiceUnit = openedInvoiceForSelectedCustomer.invoiceUnits.find(
          invoiceUnit =>
            invoiceUnit.type === CartUnitTypes.INVENTORY &&
            invoiceUnit.inventory.inventorySnapshotSourceId === newProduct.inventoryId,
        );

        if (existedInvoiceUnit) {
          dispatch(
            actionsInvoice.changeInvoiceUnitNumber(
              openedInvoiceForSelectedCustomer.id,
              existedInvoiceUnit.id,
              Number(existedInvoiceUnit.inventory.number) + 1,
              PeakModules.FrontDesk,
              false,
              true,
            ),
          );

          if (openPOSPanel) {
            openPOSPanel();
          }
        } else {
          dispatch(
            actionsInvoice.addInvoiceUnit(
              openedInvoiceForSelectedCustomer.id,
              {
                inventoryClubs: [newProduct],
                memberId: openedInvoiceForSelectedCustomer.customer?.id ?? null,
              },
              CartUnitTypes.INVENTORY,
              PeakModules.FrontDesk,
              true,
              true,
              fetchPaymentsHistory,
            ),
          );

          if (openPOSPanel) {
            openPOSPanel();
          }
        }
      } else if (selectedRegisterId && isOpenPOSPanel) {
        doAction(selectedRegisterId);
      } else {
        addDelayedAction(CREATE_INVOICE, doAction);

        if (openPOSPanel) {
          openPOSPanel();
        }
      }
    },
    [selectedRegisterId, isOpenPOSPanel, personId, dispatch, fetchPaymentsHistory, openPOSPanel],
  );

  const onSellProduct = useCallback(
    async (product: ISimpleClubInventoryItem): Promise<void> => {
      if (selectedRegisterId) {
        setIsInvoicesListLoading(true);
        await fetchInvoicesList(invoices => handleCreateOrUpdateInvoice(product, invoices));
        setIsInvoicesListLoading(false);
      } else {
        addDelayedAction(ADD_INVOICE_UNIT, invoices => {
          handleCreateOrUpdateInvoice(product, invoices);
        });

        if (openPOSPanel) {
          openPOSPanel();
        }
      }
    },
    [fetchInvoicesList, selectedRegisterId, openPOSPanel, handleCreateOrUpdateInvoice],
  );

  return (
    <CheckInBlock
      title={intl.formatMessage(messages.suggestedProductsTitle)}
      className={classes.wrapper}
    >
      {products?.size ? (
        <Grid container spacing={2} className={classes.padding}>
          {products.map(product => (
            <ProductItem
              key={`suggested-products-${product.get('id')}`}
              product={product}
              onSell={onSellProduct}
              isAddInvoiceUnitActionLoading={
                isAddInvoiceUnitActionLoading || isUpdateInvoiceUnitLoading
              }
            />
          ))}
        </Grid>
      ) : (
        <Box className="empty-section-placeholder">
          <Typography className="empty-text">
            <FormattedMessage {...messages.emptyProductsListMessage} />
          </Typography>
        </Box>
      )}
      <LoadingBackdrop isLoading={isInvoicesListLoading || isProductsLoading} />
    </CheckInBlock>
  );
};

export default SuggestedProducts;
